import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import { MainRoutes } from "common/config/route.config";
import {
  BatchExtractionResults,
  ExtractedData
} from "./BatchExtractionResults";
import { ClassificationService } from "../../../data/services/classification.service";
import { ApiResult } from "common/models";
import { ENV_CONFIG } from "common/config/env.config";
import axios from "axios";
import { msalInstance } from "common/config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider
} from "@mui/material";
import {
  Description,
  CheckCircle,
  HourglassEmpty,
  Error as ErrorIcon
} from "@mui/icons-material";
import "./BatchExtractionResults.scss";

// TypeScript interfaces for the API response
interface BatchExtractionStatusDocument {
  id: number;
  batchId: number;
  flowId: number;
  originalFilename: string;
  extractionStatus: string;
}

interface BatchExtractionStatusResponse {
  batchGUID: string;
  documents: BatchExtractionStatusDocument[];
}

class BatchExtractionService {
  public static async getBatchExtractionStatus(
    batchGUID: string
  ): Promise<ApiResult<BatchExtractionStatusResponse>> {
    try {
      // Get authentication token
      const request = {
        scopes: [process.env.REACT_APP_API_SCOPE || ""]
      };

      let tokenResult;
      try {
        tokenResult = await msalInstance.acquireTokenSilent(request);
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          tokenResult = await msalInstance.acquireTokenPopup(request);
        } else {
          throw error;
        }
      }

      const engApiUrl = process.env.REACT_APP_ENG_API_URL;
      const fullUrl = `${engApiUrl}/api/Documents/GetBatchExtractionStatus/${batchGUID}`;

      // Make the HTTP request with authentication
      const axiosResult = await axios.get(fullUrl, {
        headers: {
          Authorization: `Bearer ${tokenResult.accessToken}`,
          "Content-Type": "application/json"
        }
      });

      return new ApiResult<BatchExtractionStatusResponse>(
        axiosResult.data,
        axiosResult.status
      );
    } catch (error: any) {
      console.error("Error in getBatchExtractionStatus:", error);
      return new ApiResult<BatchExtractionStatusResponse>(
        null as any,
        error.response?.status || 500,
        error.response?.data?.message ||
          error.message ||
          "Failed to get batch extraction status"
      );
    }
  }
}

const BatchExtractionResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State for API integration
  const [batchStatus, setBatchStatus] =
    useState<BatchExtractionStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentBatchGUID, setCurrentBatchGUID] = useState<string | null>(null);

  // Get the data passed from the previous page
  const { batchData, categoryCounts, classificationName, batchGUID } =
    location.state || {};

  // Default data if no state is passed (for direct access)
  const defaultBatchData: Record<string, Record<string, ExtractedData>> = {
    Contract: {
      "0": {
        "Document Name": "Contract_Document_1",
        "Contract Number": "CNT-1234",
        "Effective Date": "2024-01-15",
        "Expiry Date": "2025-01-15",
        Status: "Extracted",
        Confidence: "92%"
      }
    },
    Invoice: {
      "0": {
        "Document Name": "Invoice_Document_1",
        "Invoice Number": "INV-5678",
        Amount: "$2,500.00",
        "Due Date": "2024-02-15",
        Status: "Extracted",
        Confidence: "89%"
      }
    }
  };

  const defaultCategoryCounts = {
    Contract: 1,
    Invoice: 1
  };

  // Load batch extraction status on component mount
  useEffect(() => {
    if (batchGUID) {
      setCurrentBatchGUID(batchGUID);
      loadBatchExtractionStatus(batchGUID);
    } else {
      console.warn(
        "No batchGUID provided from previous flow. Cannot load batch extraction status."
      );
      setError(
        "No batch GUID available. Please start a new batch extraction from the document upload and classification flow."
      );
    }
  }, [batchGUID]);

  /**
   * Load batch extraction status from API
   */
  const loadBatchExtractionStatus = async (guid: string) => {
    if (!guid) return;

    setLoading(true);
    setError(null);

    try {
      const response = await BatchExtractionService.getBatchExtractionStatus(
        guid
      );

      if (!response.hasErrors && response.value) {
        setBatchStatus(response.value);
      } else {
        setError(
          response.errors?.[0] || "Failed to load batch extraction status"
        );
      }
    } catch (err) {
      console.error("Error loading batch extraction status:", err);
      setError("An error occurred while loading batch extraction status");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get status color and icon based on extraction status
   */
  const getStatusConfig = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus.includes("completed")) {
      return {
        color: "success" as const,
        icon: <CheckCircle />,
        chipColor: "#4caf50"
      };
    } else if (
      normalizedStatus.includes("processing") ||
      normalizedStatus.includes("progress")
    ) {
      return {
        color: "warning" as const,
        icon: <HourglassEmpty />,
        chipColor: "#ff9800"
      };
    } else if (
      normalizedStatus.includes("failed") ||
      normalizedStatus.includes("error")
    ) {
      return {
        color: "error" as const,
        icon: <ErrorIcon />,
        chipColor: "#f44336"
      };
    } else {
      return {
        color: "default" as const,
        icon: <Description />,
        chipColor: "#757575"
      };
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNewUpload = () => {
    navigate(MainRoutes.DATA_CLASSIFICATION_HUB);
  };

  // dynamic back route
  const backRoute = currentBatchGUID
    ? `/classification-hub/results/${currentBatchGUID}`
    : MainRoutes.DATA_CLASSIFICATION_HISTORY;

  const handleRunBatchQualityCheck = () => {
    // Calculate actual document counts
    const actualCategoryCounts = categoryCounts || defaultCategoryCounts;
    const actualBatchData = batchData || defaultBatchData;
    const totalDocuments = Object.values(
      actualCategoryCounts as Record<string, number>
    ).reduce((sum: number, count: number) => sum + count, 0);
    const documentTypes = Object.keys(actualBatchData);

    // Generate document_wise_details based on actual uploaded documents
    const documentWiseDetails: Array<{
      doc_id: string;
      doc_type: string;
      overall_status: "pass" | "fail";
      overall_summary: string;
      quality_score: string;
      fields_validated: number;
      fields_passed: number;
      fields_failed: number;
      issues_found: Array<{
        field: string;
        validation_type: string;
        status: "pass" | "fail";
        reason: string;
      }>;
      recommendations: string[];
    }> = [];
    let docCounter = 1;

    // Document types and their sample issues
    const documentIssues = {
      Contract: [
        {
          field: "Contract Duration",
          issue: "End date is before start date",
          type: "cross-reference"
        },
        { field: "Effective Date", issue: "Invalid date format", type: "date" },
        {
          field: "Contract Value",
          issue: "Amount format inconsistency",
          type: "numeric"
        }
      ],
      Invoice: [
        {
          field: "Tax Amount",
          issue: "Tax calculation mismatch",
          type: "numeric"
        },
        { field: "Due Date", issue: "Date format inconsistency", type: "date" },
        {
          field: "Invoice Amount",
          issue: "Currency symbol missing",
          type: "numeric"
        }
      ],
      Agreement: [
        { field: "Creation Date", issue: "Invalid date format", type: "date" },
        {
          field: "Signature Date",
          issue: "Future date detected",
          type: "date"
        },
        {
          field: "Agreement Terms",
          issue: "Text truncation detected",
          type: "text-format"
        }
      ],
      Receipt: [
        {
          field: "Transaction Date",
          issue: "Date is in the future",
          type: "date"
        },
        { field: "Amount", issue: "Invalid amount format", type: "numeric" },
        {
          field: "Payment Method",
          issue: "Unrecognized payment method",
          type: "text-format"
        }
      ],
      Report: [
        {
          field: "Report Date",
          issue: "Date format validation failed",
          type: "date"
        },
        {
          field: "Data Values",
          issue: "Negative values in positive field",
          type: "numeric"
        },
        { field: "Status", issue: "Invalid status value", type: "text-format" }
      ]
    };

    for (const [docType, count] of Object.entries(actualCategoryCounts)) {
      const documentCount = count as number;
      for (let i = 0; i < documentCount; i++) {
        const docId = `DOC_${String(docCounter).padStart(3, "0")}`;
        const shouldFail = Math.random() < 0.4; // 40% chance of failure
        const possibleIssues =
          documentIssues[docType as keyof typeof documentIssues] ||
          documentIssues.Contract;
        const selectedIssue =
          possibleIssues[Math.floor(Math.random() * possibleIssues.length)];

        const fieldsValidated = Math.floor(Math.random() * 4) + 5; // 5-8 fields
        const fieldsFailed = shouldFail ? Math.floor(Math.random() * 2) + 1 : 0; // 1-2 failures if failing
        const fieldsPassed = fieldsValidated - fieldsFailed;
        const qualityScore = Math.floor((fieldsPassed / fieldsValidated) * 100);

        const document = {
          doc_id: docId,
          doc_type: docType,
          overall_status: shouldFail ? ("fail" as const) : ("pass" as const),
          overall_summary: shouldFail
            ? `The document has ${fieldsFailed} critical issue(s) that need to be addressed. ${fieldsPassed} fields passed validation, but ${selectedIssue.field.toLowerCase()} issues were detected requiring correction.`
            : `The document passed all validation checks with ${fieldsValidated} fields validated successfully. All date, numeric, and text fields meet quality standards.`,
          quality_score: `${qualityScore}%`,
          fields_validated: fieldsValidated,
          fields_passed: fieldsPassed,
          fields_failed: fieldsFailed,
          issues_found: shouldFail
            ? [
                {
                  field: selectedIssue.field,
                  validation_type: selectedIssue.type,
                  status: "fail" as const,
                  reason: selectedIssue.issue
                }
              ]
            : [],
          recommendations: shouldFail
            ? [
                `Correct the ${selectedIssue.field.toLowerCase()} in the identified field`,
                `Verify all ${selectedIssue.type} fields follow standard format`
              ]
            : []
        };

        documentWiseDetails.push(document);
        docCounter++;
      }
    }

    const passedDocs = documentWiseDetails.filter(
      (doc) => doc.overall_status === "pass"
    ).length;
    const failedDocs = documentWiseDetails.filter(
      (doc) => doc.overall_status === "fail"
    ).length;
    const overallQualityScore = Math.floor((passedDocs / totalDocuments) * 100);

    // Generate sample quality assessment data
    const sampleQualityData = {
      executive_summary: {
        summary: `Quality assessment completed for batch extraction results. ${totalDocuments} documents were processed with ${passedDocs} documents passing all validation checks and ${failedDocs} documents requiring attention for various issues including date formats, amount calculations, and field validations.`,
        total_documents_checked: totalDocuments,
        total_document_types: documentTypes.length,
        documents_passed: passedDocs,
        documents_failed: failedDocs,
        overall_quality_score: `${overallQualityScore}%`,
        critical_issues_count: failedDocs,
        batch_id: `BATCH_${Date.now()}`
      },
      document_type_breakdown: documentTypes.map((docType) => {
        const typeDocuments = documentWiseDetails.filter(
          (doc) => doc.doc_type === docType
        );
        const typePassed = typeDocuments.filter(
          (doc) => doc.overall_status === "pass"
        ).length;
        const typeFailed = typeDocuments.filter(
          (doc) => doc.overall_status === "fail"
        ).length;
        const typeQualityScore =
          typeDocuments.length > 0
            ? Math.floor((typePassed / typeDocuments.length) * 100)
            : 0;

        return {
          doc_type: docType,
          total_documents: typeDocuments.length,
          documents_passed: typePassed,
          documents_failed: typeFailed,
          quality_score: `${typeQualityScore}%`,
          fields_validated: Object.keys(
            actualBatchData[docType]
              ? Object.values(actualBatchData[docType])[0] || {}
              : {}
          ).filter((key) => key !== "Status" && key !== "Confidence").length,
          field_wise_analysis: {},
          critical_issues: [],
          recommendations: [
            `Improve ${docType} data validation`,
            `Review ${docType} extraction accuracy`
          ]
        };
      }),
      document_wise_details: documentWiseDetails,
      field_level_analysis: {
        date_validations: {
          total_date_fields: 5,
          passed: 4,
          failed: 1,
          common_issues_with_reason: ["Invalid date format"],
          details: []
        },
        numeric_validations: {
          total_numeric_fields: 3,
          passed: 3,
          failed: 0,
          common_issues: [],
          statistics: {
            average_values: {},
            ranges: {}
          }
        },
        text_validations: {
          total_text_fields: 8,
          null_fields_found: 1,
          completeness_rate: "87.5%"
        }
      },
      critical_issues_summary: {
        high_priority: documentWiseDetails
          .filter(
            (doc) =>
              doc.issues_found.length > 0 &&
              (doc.issues_found[0].validation_type === "date" ||
                doc.issues_found[0].validation_type === "cross-reference")
          )
          .map((doc) => ({
            doc_id: doc.doc_id,
            field: doc.issues_found[0].field,
            issue: doc.issues_found[0].reason,
            severity: "high" as const,
            impact: "Blocks document processing and validation",
            doc_type: doc.doc_type
          })),
        medium_priority: documentWiseDetails
          .filter(
            (doc) =>
              doc.issues_found.length > 0 &&
              doc.issues_found[0].validation_type === "numeric"
          )
          .map((doc) => ({
            doc_id: doc.doc_id,
            field: doc.issues_found[0].field,
            issue: doc.issues_found[0].reason,
            severity: "medium" as const,
            impact: "May cause calculation errors",
            doc_type: doc.doc_type
          })),
        low_priority: documentWiseDetails
          .filter(
            (doc) =>
              doc.issues_found.length > 0 &&
              doc.issues_found[0].validation_type === "text-format"
          )
          .map((doc) => ({
            doc_id: doc.doc_id,
            field: doc.issues_found[0].field,
            issue: doc.issues_found[0].reason,
            severity: "low" as const,
            impact: "Minor processing inconvenience",
            doc_type: doc.doc_type
          }))
      },
      recommendations: {
        document_type_specific: documentTypes.reduce((acc, docType) => {
          acc[docType] = [
            `Standardize ${docType.toLowerCase()} field formats`,
            `Implement ${docType.toLowerCase()} specific validation rules`
          ];
          return acc;
        }, {} as Record<string, string[]>),
        overall: [
          "Implement stricter validation rules",
          "Add data quality monitoring",
          "Review extraction confidence thresholds"
        ]
      },
      quality_metrics: {
        documents_checked: totalDocuments,
        quality_checks_passed: passedDocs,
        quality_checks_failed: failedDocs,
        overall_quality_score: `${overallQualityScore}%`,
        pass_rate_by_doc_type: documentTypes.reduce((acc, docType) => {
          const typeDocuments = documentWiseDetails.filter(
            (doc) => doc.doc_type === docType
          );
          const typePassed = typeDocuments.filter(
            (doc) => doc.overall_status === "pass"
          ).length;
          acc[docType] =
            typeDocuments.length > 0
              ? `${Math.floor((typePassed / typeDocuments.length) * 100)}%`
              : "0%";
          return acc;
        }, {} as Record<string, string>)
      }
    };

    navigate(MainRoutes.BATCH_QUALITY_ASSESSMENT, {
      state: {
        batchQualityResults: sampleQualityData,
        batchData: batchData || defaultBatchData,
        categoryCounts: categoryCounts || defaultCategoryCounts,
        batchGUID: currentBatchGUID
      }
    });
  };

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="batch-extraction-results-page">
        <HubHeader
          title="Quality Hub"
          description="Batch extraction results and quality assessment"
          showBackButton={true}
          backRoute={backRoute}
        />

        <BatchExtractionResults
          batchData={batchData || defaultBatchData}
          categoryCounts={categoryCounts || defaultCategoryCounts}
          batchGUID={currentBatchGUID || undefined}
          onBack={handleBack}
          onNewUpload={handleNewUpload}
          onRunBatchQualityCheck={handleRunBatchQualityCheck}
        />
      </div>
    </>
  );
};

export default BatchExtractionResultsPage;
